import React from "react";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import ActionButton from "@quest_editor/components/Buttons/ActionButton";
import BackButton from "@quest_editor/components/Buttons/BackButton";
import Breadcrumb from "@quest_editor/components/Breadcrumb";
import PromotionTypeChip from "@quest_editor/components/Chips/PromotionTypeChip";
import PromotionStatusChip from "@quest_editor/components/Chips/PromotionStatusChip";
import {
  usePromotion,
  useQuest,
  useUpdateQuest,
  UpdateQuestInput,
} from "@quest_editor/mocks";
import { IncentiveType, IncentiveTypeValue } from "@quest_editor/constants";
import BasicInfoSection from "./BasicInfoSection";
import NoticeOptionSection from "./NoticeOptionSection";
import ImageUploadOptionSection from "./ImageUploadOptionSection";
import CheckInstructionSection from "./CheckInstructionSection";
import ForRniSection from "./ForRniSection";

const splitDateTime = (
  dateTimeStr: string | null | undefined,
): { date: string; time: string } => {
  if (!dateTimeStr) return { date: "", time: "" };
  const dt = new Date(dateTimeStr);
  const date = dt.toISOString().split("T")[0];
  const time = dt.toTimeString().slice(0, 5);
  return { date, time };
};

const combineDateTime = (date: string, time: string): string | null => {
  if (!date) return null;
  return `${date}T${time}:00`;
};

const QuestEdit = () => {
  const { t } = useTranslation("questEditor");
  const navigate = useNavigate();
  const { id: questId } = useParams<{ id: string }>();

  const { data: questData } = useQuest(questId);
  const quest = questData?.quest;

  const { data: promotionData } = usePromotion(quest?.promotionId);
  const promotion = promotionData?.promotion;

  const { updateQuest } = useUpdateQuest();

  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [note, setNote] = React.useState("");

  const [incentiveType, setIncentiveType] = React.useState<IncentiveTypeValue>(
    IncentiveType.TAMARU,
  );
  const [tamaruPoint, setTamaruPoint] = React.useState<string>("");
  const [prizeName, setPrizeName] = React.useState("");

  const [imageSelection, setImageSelection] = React.useState("upload");
  const [fileName, setFileName] = React.useState("");
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [imageUrl, setImageUrl] = React.useState("");

  const [fileNamePop, setFileNamePop] = React.useState("");
  const [previewUrlPop, setPreviewUrlPop] = React.useState<string | null>(null);
  const [popupImageUrl, setPopupImageUrl] = React.useState("");

  const [startDate, setStartDate] = React.useState("");
  const [startTime, setStartTime] = React.useState("08:00");
  const [endDate, setEndDate] = React.useState("");
  const endTime = "23:59";

  const [productCheckNotice, setProductCheckNotice] = React.useState("");
  const [checkerNote, setCheckerNote] = React.useState("");
  const [receiptCheckDescription, setReceiptCheckDescription] =
    React.useState("");

  const [slackChannel, setSlackChannel] = React.useState("");
  const [memo, setMemo] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const fileInputRefPop = React.useRef<HTMLInputElement>(null);

  const adminSetting = {
    alreadySet: false,
    fileName: "サンプルファイル名",
  };

  const codeAdminUrl = "";

  React.useEffect(() => {
    if (quest) {
      setName(quest.name);
      setDescription(quest.description);
      setNote(quest.note ?? "");
      setIncentiveType(quest.incentiveType);
      setTamaruPoint(quest.tamaruPoint?.toString() ?? "");
      setPrizeName(quest.prizeName ?? "");
      setImageUrl(quest.imageUrl);
      setPreviewUrl(quest.imageUrl || null);
      setPopupImageUrl(quest.popupImageUrl ?? "");
      setPreviewUrlPop(quest.popupImageUrl || null);
      setProductCheckNotice(quest.productCheckNotice ?? "");
      setCheckerNote(quest.checkerNote ?? "");
      setReceiptCheckDescription(quest.receiptCheckDescription ?? "");
      setSlackChannel(quest.slackChannel ?? "");
      setMemo(quest.memo ?? "");

      const start = splitDateTime(quest.startAt);
      setStartDate(start.date);
      setStartTime(start.time || "08:00");

      const end = splitDateTime(quest.endAt);
      setEndDate(end.date);
    }
  }, [quest]);

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      setFileName(file.name);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setImageUrl(url);
    }
  };

  const handleFileChangePop = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      setFileNamePop(file.name);
      const url = URL.createObjectURL(file);
      setPreviewUrlPop(url);
      setPopupImageUrl(url);
    }
  };

  const handleIncentiveTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setIncentiveType(value === "1" ? IncentiveType.TAMARU : IncentiveType.PRIZE);
  };

  const save = async () => {
    if (!questId) return;

    const input: UpdateQuestInput = {
      id: questId,
      name,
      description,
      note,
      incentiveType,
      tamaruPoint:
        incentiveType === IncentiveType.TAMARU
          ? tamaruPoint
            ? parseInt(tamaruPoint, 10)
            : null
          : null,
      prizeName: incentiveType === IncentiveType.PRIZE ? prizeName : null,
      imageUrl,
      popupImageUrl: popupImageUrl || undefined,
      startAt: combineDateTime(startDate, startTime),
      endAt: combineDateTime(endDate, endTime),
      productCheckNotice: productCheckNotice || undefined,
      checkerNote: checkerNote || undefined,
      receiptCheckDescription: receiptCheckDescription || undefined,
      slackChannel: slackChannel || undefined,
      memo: memo || undefined,
    };

    await updateQuest(input);
  };

  const handleSaveAndBackToDetail = async () => {
    if (!quest || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await save();
      navigate(`/quest_editor/promotion/${quest.promotionId}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveAndGoToNext = async () => {
    if (!questId || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await save();
      navigate(`/quest_editor/quest/${questId}/edit/target`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!quest || !promotion) {
    return <Typography>{t("common.label.not_found")}</Typography>;
  }

  return (
    <Stack direction="column" spacing={3} sx={{ flex: 1, minHeight: 0 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Stack direction="column" spacing={2} alignItems="flex-start">
          <Breadcrumb items={[{ label: promotion.name, to: `/quest_editor/promotion/${promotion.id}` }, { label: quest.name }]} />
          <Stack direction="row" spacing={1}>
            <PromotionTypeChip type={promotion.type} />
            <PromotionStatusChip status={promotion.status} />
          </Stack>
        </Stack>
      </Stack>

      <Stack direction="column" spacing={2}>
        <BasicInfoSection
          name={name}
          setName={setName}
          description={description}
          setDescription={setDescription}
          note={note}
          setNote={setNote}
          incentiveType={incentiveType}
          onIncentiveTypeChange={handleIncentiveTypeChange}
          tamaruPoint={tamaruPoint}
          setTamaruPoint={setTamaruPoint}
          prizeName={prizeName}
          setPrizeName={setPrizeName}
          imageSelection={imageSelection}
          setImageSelection={setImageSelection}
          fileName={fileName}
          previewUrl={previewUrl}
          onFileChange={handleFileChange}
          fileInputRef={fileInputRef}
          startDate={startDate}
          setStartDate={setStartDate}
          startTime={startTime}
          setStartTime={setStartTime}
          endDate={endDate}
          setEndDate={setEndDate}
          endTime={endTime}
        />

        <NoticeOptionSection
          fileNamePop={fileNamePop}
          previewUrlPop={previewUrlPop}
          onFileChangePop={handleFileChangePop}
          fileInputRefPop={fileInputRefPop}
          name={name}
        />

        <ImageUploadOptionSection
          productCheckNotice={productCheckNotice}
          setProductCheckNotice={setProductCheckNotice}
        />

        <CheckInstructionSection
          checkerNote={checkerNote}
          setCheckerNote={setCheckerNote}
          receiptCheckDescription={receiptCheckDescription}
          setReceiptCheckDescription={setReceiptCheckDescription}
          adminSetting={adminSetting}
          codeAdminUrl={codeAdminUrl}
        />

        <ForRniSection
          slackChannel={slackChannel}
          setSlackChannel={setSlackChannel}
          memo={memo}
          setMemo={setMemo}
        />

        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          alignItems="center"
          sx={{ position: "relative" }}
        >
          <BackButton
            component={RouterLink}
            to={`/quest_editor/promotion/${promotion.id}`}
            sx={{ position: "absolute", left: 0 }}
          >
            {t("quest_edit.button.back")}
          </BackButton>

          <Stack direction="row" spacing={2}>
            <ActionButton
              onClick={handleSaveAndBackToDetail}
              disabled={isSubmitting}
            >
              {t("quest_edit.button.save_go_back")}
            </ActionButton>
            <ActionButton
              onClick={handleSaveAndGoToNext}
              disabled={isSubmitting}
            >
              {t("quest_edit.button.save_go_next")}
            </ActionButton>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
export default QuestEdit;
